import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectProps } from '@mui/material';
import React from 'react';

import { EntityOnChange } from 'app/type';

type EnumType = Record<string, number | string>;

type EnumValues<T> = T extends Record<string, infer X> ? X : never;

interface EnumSelectorProps<E extends object, T extends EnumType>
    extends Omit<SelectProps<EnumValues<T> | ''>, 'name' | 'onChange' | 'value'> {
    theEnum: T;
    value: E;
    name: string & keyof E;
    size?: 'medium' | 'small';
    helperText?: string;
    labelDictionary?: Record<EnumValues<T>, React.ReactNode>;
    onChange: EntityOnChange<E, EnumValues<T> | undefined>;
}

export const EnumSelector = <E extends object, T extends EnumType>(
    props: EnumSelectorProps<E, T>,
): React.ReactElement => {
    const { name, onChange, helperText, required, theEnum, size, labelDictionary, value, ...rest } = props;

    const innerOnChange: SelectProps<EnumValues<T> | ''>['onChange'] = (evt) => {
        const { name: evtName, value: evtVal } = evt.target;
        onChange(evt, evtName as never, '' === evtVal ? undefined : (evtVal as EnumValues<T>));
    };

    const data = value[name] as EnumValues<T> | undefined;

    return (
        <FormControl style={{ minWidth: '5em' }} size={size}>
            <InputLabel required={required} error={props.error}>
                {props.label}
            </InputLabel>
            <Select {...rest} name={name} onChange={innerOnChange} value={undefined !== data ? data : ''}>
                {Object.keys(theEnum)
                    .filter((val) => isNaN(Number(val)))
                    .map((enumKey) => {
                        const theKey = enumKey as keyof T;
                        const enumVal = theEnum[theKey] as EnumValues<T>;

                        return (
                            <MenuItem key={theKey as string} value={enumVal}>
                                {labelDictionary?.[enumVal] || enumKey}
                            </MenuItem>
                        );
                    })}
            </Select>
            <FormHelperText error={props.error}>{helperText}</FormHelperText>
        </FormControl>
    );
};
